/**
 * Sprint 12+M5.17-t (22.07.2026) — Vimeo-Embed mit DSGVO-Consent + Drip.
 *
 * Sprint 12+M5.20 (24.07.2026) — iOS-Safari-Playback-Fix.
 *   Player.js hat via `new Vimeo.Player(div, {id, h, ...})` das iframe
 *   dynamisch erzeugt — auf iPhone Safari fehlten dabei `playsinline`
 *   und `allow="fullscreen"`, wodurch der Tap auf Play die View kurz
 *   in Fullscreen schickte und schwarz zurueckkam.
 *   Fix: Wir rendern ein STATISCHES <iframe> mit expliziten URL-Params
 *   (playsinline=1, dnt=1, h=hash) und haengen Player.js NACHTRAEGLICH
 *   fuer Tracking an das bereits geladene iframe.
 *
 * States (nach Prioritaet):
 *   1. LOCKED — Video-Unlock-Datum noch nicht erreicht (Drip-System)
 *      -> Preview-Thumbnail (vumbnail.com) + Countdown "In X Tagen"
 *   2. AWAITING_CONSENT — freigeschaltet aber User hat Vimeo-Cookies noch
 *      nicht akzeptiert -> Custom-Consent-Gate mit "Video laden"-CTA
 *   3. LOADED — statisches iframe rendert
 *
 * Meta-Tracking (nur wenn `window.fbq` verfuegbar = Marketing-Consent
 * bereits erteilt via Cookie-Banner):
 *   - VideoPlay: Custom-Event mit `video_key`, `video_id`
 *   - VideoComplete: dito, Signal fuer Lookalike-Audiences
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Play, Lock } from "lucide-react";
import { VIMEO_VIDEOS, vimeoThumbnailUrl } from "@/config/vimeo";

const PLAYER_SCRIPT_SRC = "https://player.vimeo.com/api/player.js";

function ensurePlayerScript() {
  return new Promise((resolve) => {
    if (window.Vimeo && window.Vimeo.Player) {
      resolve();
      return;
    }
    const existing = document.querySelector(`script[src="${PLAYER_SCRIPT_SRC}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve(), { once: true });
      return;
    }
    const s = document.createElement("script");
    s.src = PLAYER_SCRIPT_SRC;
    s.async = true;
    s.onload = () => resolve();
    document.body.appendChild(s);
  });
}

function trackVideoEvent(name, video, videoKey) {
  try {
    // Client-Side Meta Pixel — feuert nur wenn Consent erteilt
    if (typeof window.fbq === "function") {
      window.fbq("trackCustom", name, {
        video_key: videoKey,
        video_id: video.id,
        video_title: video.title,
      });
    }
    // dataLayer parallel fuer GTM / interne Analytics
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: name.toLowerCase(),
      video_key: videoKey,
      video_id: video.id,
    });
  } catch {
    /* Tracking darf niemals den Video-Flow blockieren */
  }
}

/** Baut die Player-URL mit iOS-safen Params. */
function buildPlayerUrl(video) {
  const params = new URLSearchParams({
    playsinline: "1",
    dnt: "1",
    title: "0",
    byline: "0",
    portrait: "0",
  });
  if (video.hash) params.set("h", video.hash);
  return `https://player.vimeo.com/video/${video.id}?${params.toString()}`;
}

export default function VimeoEmbed({ videoKey, userSignupDate, overrideOffsetDays, unlockAt }) {
  const [consented, setConsented] = useState(false);
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [daysUntilUnlock, setDaysUntilUnlock] = useState(0);
  const iframeRef = useRef(null);
  const playerRef = useRef(null);

  const video = VIMEO_VIDEOS[videoKey];
  const playerUrl = useMemo(() => (video?.id ? buildPlayerUrl(video) : null), [video]);
  // Sprint M5.39 (30.07.2026) — Block B1: Erlaubt tier-abhaengige Drip-Offsets.
  const offsetDays = typeof overrideOffsetDays === "number" ? overrideOffsetDays : (video?.unlockOffsetDays ?? 0);

  // Sprint V101.4 (22.09.2026) — Single Source of Truth:
  // Wenn `unlockAt` (ISO-String vom Backend) gesetzt ist, hat er Vorrang.
  // Fallback auf die alte offsetDays-Berechnung, damit Bestands-Aufrufe
  // (z.B. Bridge-Videos, Pionier-Grid) unveraendert weiterlaufen.
  useEffect(() => {
    if (!video) return;
    if (unlockAt !== undefined && unlockAt !== null) {
      const unlockDate = new Date(unlockAt);
      const now = new Date();
      const diffMs = unlockDate - now;
      if (diffMs <= 0) {
        setIsUnlocked(true);
        setDaysUntilUnlock(0);
      } else {
        setIsUnlocked(false);
        // Kalendertag-Differenz (nicht 24h-Fenster) — matcht Backend V101-Semantik.
        const dayMs = 24 * 60 * 60 * 1000;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const target = new Date(unlockDate);
        target.setHours(0, 0, 0, 0);
        const diffDays = Math.round((target - today) / dayMs);
        setDaysUntilUnlock(Math.max(diffDays, 1));
      }
      return;
    }
    if (!userSignupDate) {
      // Fallback: Wenn kein Signup-Date vorhanden, nur Tag-0-Videos freischalten
      setIsUnlocked(offsetDays === 0);
      setDaysUntilUnlock(offsetDays);
      return;
    }
    const signup = new Date(userSignupDate);
    const unlockAtLegacy = new Date(signup.getTime() + offsetDays * 24 * 60 * 60 * 1000);
    const now = new Date();
    const diffMs = unlockAtLegacy - now;
    if (diffMs <= 0) {
      setIsUnlocked(true);
      setDaysUntilUnlock(0);
    } else {
      setIsUnlocked(false);
      setDaysUntilUnlock(Math.ceil(diffMs / (24 * 60 * 60 * 1000)));
    }
  }, [userSignupDate, video, offsetDays, unlockAt]);

  // Player.js an das bereits gemountete iframe haengen — nur fuer Tracking.
  useEffect(() => {
    if (!isUnlocked || !consented || !video?.id || !iframeRef.current) return;
    let destroyed = false;
    (async () => {
      await ensurePlayerScript();
      if (destroyed || !iframeRef.current || playerRef.current) return;
      try {
        // Player.js findet das existierende iframe automatisch.
        playerRef.current = new window.Vimeo.Player(iframeRef.current);
        playerRef.current.on("play", () => trackVideoEvent("VideoPlay", video, videoKey));
        playerRef.current.on("ended", () => trackVideoEvent("VideoComplete", video, videoKey));
      } catch (err) {
        console.warn("Vimeo player attach failed", err);
      }
    })();
    return () => {
      destroyed = true;
      try {
        playerRef.current?.destroy();
      } catch {
        /* noop */
      }
      playerRef.current = null;
    };
  }, [isUnlocked, consented, video, videoKey]);

  if (!video) {
    return null;
  }

  // ============ 1. LOCKED ============
  if (!isUnlocked) {
    const thumb = vimeoThumbnailUrl(video);
    return (
      <div
        className="relative w-full rounded-xl overflow-hidden bg-slate-900 shadow-lg"
        style={{ aspectRatio: "16 / 9" }}
        data-testid={`vimeo-locked-${videoKey}`}
      >
        {thumb && (
          <img
            src={thumb}
            alt={video.title}
            className="absolute inset-0 w-full h-full object-cover"
            loading="lazy"
          />
        )}
        {/* Sprint M5.24.4: Starker Dunkel-Filter, damit das Schloss visuell dominiert */}
        <div className="absolute inset-0 bg-slate-950/75" aria-hidden="true" />
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-4">
          <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-white/10 border border-white/25 backdrop-blur-sm flex items-center justify-center mb-3">
            <Lock className="w-7 h-7 sm:w-9 sm:h-9 text-white" strokeWidth={2.25} />
          </div>
          <h3 className="text-white font-bold text-lg sm:text-xl drop-shadow">{video.title}</h3>
          <p className="text-white/85 text-sm mt-1 font-medium">
            {daysUntilUnlock <= 1
              ? "Morgen freigeschaltet"
              : `In ${daysUntilUnlock} Tagen freigeschaltet`}
          </p>
          <p className="text-white/60 text-xs mt-2 max-w-xs">{video.description}</p>
        </div>
      </div>
    );
  }

  // ============ 2. AWAITING CONSENT ============
  if (!consented) {
    const thumb = vimeoThumbnailUrl(video);
    return (
      <div
        className="relative w-full rounded-xl overflow-hidden bg-black"
        style={{ aspectRatio: "16 / 9" }}
        data-testid={`vimeo-consent-${videoKey}`}
      >
        {thumb && (
          <img
            src={thumb}
            alt={video.title}
            className="absolute inset-0 w-full h-full object-cover opacity-50"
            loading="lazy"
          />
        )}
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-6 bg-black/70">
          <h3 className="text-white font-bold text-lg sm:text-xl mb-2">{video.title}</h3>
          <p className="text-white/70 text-sm max-w-sm mb-5">
            Wir zeigen dieses Video über Vimeo. Beim Aktivieren lädt Vimeo
            Cookies auf deinem Gerät.
          </p>
          <button
            type="button"
            onClick={() => {
              setConsented(true);
              trackVideoEvent("VideoUnlocked", video, videoKey);
            }}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#4563BB] hover:bg-[#3a54a3] text-white font-semibold shadow-sm transition-colors"
            data-testid={`vimeo-consent-btn-${videoKey}`}
          >
            <Play className="w-4 h-4" fill="currentColor" />
            Video laden
          </button>
        </div>
      </div>
    );
  }

  // ============ 3. LOADED ============
  // Sprint 12+M5.20 (24.07.2026) — Statisches iframe mit iOS-safen Params.
  return (
    <div
      className="relative w-full rounded-xl overflow-hidden bg-black"
      style={{ aspectRatio: "16 / 9" }}
      data-testid={`vimeo-player-${videoKey}`}
    >
      <iframe
        ref={iframeRef}
        src={playerUrl}
        title={video.title}
        className="absolute inset-0 w-full h-full"
        frameBorder="0"
        allow="autoplay; fullscreen; picture-in-picture; encrypted-media; clipboard-write"
        allowFullScreen
        referrerPolicy="strict-origin-when-cross-origin"
        data-testid={`vimeo-iframe-${videoKey}`}
      />
    </div>
  );
}
